
import React from "react";
import classNames from "classnames";
import { useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';

import {
  Collapse,
  NavbarBrand,
  Navbar,
  Nav,
  Container,
  Input,
  Modal,
} from "reactstrap";

import Settings from "./Settings";
import { I18N_LANGUAGE_KEY } from "i18n";
import { useGetAccount, useUpdateSelectedTenant } from "api/account/service";
import Notification from "./Notification";
import AccountUserMenu from "./AccountUserMenu";
import NavbarLanguageSelector from "./NavbarLanguageSelector";
import { useGetSearch } from "api/system/service";
import NavbarHelp from "./NavbarHelp";
import FutureFeature from "./FutureFeature";
import { RiCarFill } from "react-icons/ri";
import { RxHamburgerMenu } from "react-icons/rx";
import { IoClose } from "react-icons/io5";
import { MdFullscreen, MdOutlineFullscreenExit } from "react-icons/md";
import { useAdminNavbarAppearance } from "./AdminNavbarAppearanceContext";
import {
  theme2NavbarLightSurfaceSearchTokens,
  THEME2_TOOLBAR_TRIGGER_HOVER_ICON_DARK_SURFACE,
  THEME2_TOOLBAR_TRIGGER_HOVER_ICON_LIGHT_SURFACE,
} from "./adminNavbarTheme2Appearance";
import { isWebMakerRouteBlocked } from "utils/permissionUtils";

/** Tema 2 drawer: xl altı (≤1199, mobil 425 dahil); hamburger nav-open, mini değil */
const THEME2_DRAWER_LAYOUT_MEDIA_QUERY = "(max-width: 1199.98px)";
/** Mobil üst bar (kebab, logo2, sıra): Bootstrap collapse ile aynı (< lg) */
const MOBILE_NAVBAR_LAYOUT_MEDIA_QUERY = "(max-width: 991.98px)";
const THEME2_MOBILE_NAVBAR_LOGO_SRC = `${process.env.PUBLIC_URL}/logo2.svg`;
const MOBILE_NAVBAR_SHELL_SELECTOR =
  ".navbar.admin-navbar-shell-theme-1, .navbar.admin-navbar-shell-theme-2";

function useMediaQueryMatch(query) {
  const [matches, setMatches] = React.useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia(query).matches;
  });

  React.useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const mq = window.matchMedia(query);
    const sync = () => setMatches(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, [query]);

  return matches;
}

/** Tema 2 sidebar ile aynı kök; üst çubuk yüksekliği SCSS ile sabit (lg+). */
const THEME2_SHELL_NAV_TYPOGRAPHY = {
  fontSize: "var(--sidebar-shell-theme-2-nav-font-size, 14px)",
  lineHeight: "var(--sidebar-shell-theme-2-nav-line-height, 1.65)",
};

const AdminNavbar = ({
  location,
  toggleSidebar,
  sidebarOpened,
  handleMiniClick,
  activeColor,
  handleActiveClick,
  sidebarMini,
  routes,
  logo,
  closeSidebar,
  embedInAppearancePreview = false,
}) => {
  const ctxAppearance = useAdminNavbarAppearance();
  const isTheme2Shell = ctxAppearance.navbarShellClassNames?.["admin-navbar-shell-theme-2"] === true;
  const [isLightTheme, setIsLightTheme] = React.useState(() =>
    typeof document !== "undefined" && document.body.classList.contains("white-content")
  );
  const appearance = React.useMemo(() => {
    let next = ctxAppearance;
    if (isTheme2Shell && isLightTheme) {
      next = { ...ctxAppearance, ...theme2NavbarLightSurfaceSearchTokens };
    }
    const light = next.toolbarIconColorLight;
    const dark = next.toolbarIconColorDark;
    if (isTheme2Shell && light != null && dark != null) {
      const iconColor = isLightTheme ? light : dark;
      const activeLight = next.toolbarIconColorActiveLight;
      const activeDark = next.toolbarIconColorActiveDark ?? next.toolbarIconColorActive;
      const activeIconColor = isLightTheme
        ? (activeLight ?? "#000000")
        : (activeDark ?? "#ffffff");
      next = {
        ...next,
        toolbarIconColor: iconColor,
        toolbarIconColorActive: activeIconColor,
        searchIcon: { ...next.searchIcon, color: iconColor },
        modalSearchIcon: { ...next.modalSearchIcon, color: iconColor },
        modalCloseIcon: { ...next.modalCloseIcon, color: iconColor },
        emptyStateIcon: { ...next.emptyStateIcon, color: iconColor },
      };
    }
    return next;
  }, [ctxAppearance, isTheme2Shell, isLightTheme]);
  const theme2DrawerLayoutMq = useMediaQueryMatch(THEME2_DRAWER_LAYOUT_MEDIA_QUERY);
  const isMobileNavbarLayout = useMediaQueryMatch(MOBILE_NAVBAR_LAYOUT_MEDIA_QUERY);
  const theme2DrawerLayout = isTheme2Shell && theme2DrawerLayoutMq;
  const sidebarHamburgerIsMiniToggle =
    appearance.sidebarTriggerAction === "mini" && !theme2DrawerLayout;
  const lightNavbarSurface = isTheme2Shell && isLightTheme;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [collapseOpen, setCollapseOpen] = React.useState(false);
  const [color, setColor] = React.useState("navbar-transparent");
  /** Tema 1 mobil: collapse açıkken navbar `bg-white` — hesap tetikleyici metni koyu olmalı */
  const accountMenuLightSurface = isTheme2Shell ? isLightTheme : color === "bg-white";
  /** Üst çubuk logo filtresi: açık zemin → koyu logo; koyu zemin → açık logo */
  const logoOnLightSurface = isTheme2Shell ? isLightTheme : color === "bg-white";
  /** Tema 2: üst çubukta logo yok (mobil dahil); marka yazısı mobilde kalabilir */
  const displayNavbarLogo =
    appearance.showNavbarLogoImage && !isTheme2Shell;
  const displayNavbarBrand =
    appearance.showNavbarBrandText !== false &&
    (!isTheme2Shell || isMobileNavbarLayout);
  const navbarLogoSrc = React.useMemo(() => {
    if (!logo?.imgSrc) return "";
    if (isTheme2Shell && isMobileNavbarLayout) return THEME2_MOBILE_NAVBAR_LOGO_SRC;
    return logo.imgSrc;
  }, [logo?.imgSrc, isTheme2Shell, isMobileNavbarLayout]);
  const navbarLogoImgStyle = React.useMemo(
    () =>
      logoOnLightSurface
        ? appearance.logoImgLightSurface ?? appearance.logoImg
        : appearance.logoImg,
    [appearance.logoImg, appearance.logoImgLightSurface, logoOnLightSurface]
  );
  const mobileKebabIconColor = React.useMemo(() => {
    if (isTheme2Shell) {
      return (
        appearance.toolbarIconColor ??
        (lightNavbarSurface ? "rgba(0, 0, 0, 0.55)" : "#808590")
      );
    }
    return color === "bg-white" ? "#32325d" : "rgba(255, 255, 255, 0.9)";
  }, [isTheme2Shell, appearance.toolbarIconColor, lightNavbarSurface, color]);
  const [modalSearch, setModalSearch] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [searchSections, setSearchSections] = React.useState([]);
  const searchInputRef = React.useRef(null);
  const { data: searchData, isLoading: searchLoading } = useGetSearch(searchQuery);
  const { data: account } = useGetAccount();
  const [helpOpen, setHelpOpen] = React.useState(false);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [t2FullscreenHoverDesktop, setT2FullscreenHoverDesktop] = React.useState(false);
  const [t2FullscreenHoverMobile, setT2FullscreenHoverMobile] = React.useState(false);

  const fullscreenIconPx = React.useMemo(() => {
    const raw = appearance.toolbarIconFontSize;
    let base = 18;
    if (typeof raw === "string" && raw.endsWith("px")) {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n)) base = n;
    }
    return base + 8;
  }, [appearance.toolbarIconFontSize]);

  const t2FullscreenTriggerIconColor = React.useCallback(
    (hovered) => {
      if (hovered && lightNavbarSurface) return THEME2_TOOLBAR_TRIGGER_HOVER_ICON_LIGHT_SURFACE;
      if (hovered && !lightNavbarSurface) return THEME2_TOOLBAR_TRIGGER_HOVER_ICON_DARK_SURFACE;
      return appearance.toolbarIconColor ?? (lightNavbarSurface ? "rgba(0, 0, 0, 0.55)" : "white");
    },
    [appearance.toolbarIconColor, lightNavbarSurface]
  );

  React.useEffect(() => {
    const syncFullscreen = () => {
      const el =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.msFullscreenElement;
      setIsFullscreen(!!el);
    };
    syncFullscreen();
    document.addEventListener("fullscreenchange", syncFullscreen);
    document.addEventListener("webkitfullscreenchange", syncFullscreen);
    document.addEventListener("MSFullscreenChange", syncFullscreen);
    return () => {
      document.removeEventListener("fullscreenchange", syncFullscreen);
      document.removeEventListener("webkitfullscreenchange", syncFullscreen);
      document.removeEventListener("MSFullscreenChange", syncFullscreen);
    };
  }, []);

  const toggleDocumentFullscreen = React.useCallback(async () => {
    const root = document.documentElement;
    const active =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.msFullscreenElement;
    try {
      if (!active) {
        if (root.requestFullscreen) await root.requestFullscreen();
        else if (root.webkitRequestFullscreen) root.webkitRequestFullscreen();
        else if (root.msRequestFullscreen) root.msRequestFullscreen();
      } else {
        if (document.exitFullscreen) await document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
        else if (document.msExitFullscreen) document.msExitFullscreen();
      }
    } catch (_) {
      // unsupported or user denied
    }
  }, []);

  React.useEffect(() => {
    const checkTheme = () => {
      setIsLightTheme(document.body.classList.contains('white-content'));
    };
    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  // Dil değiştirme fonksiyonu
  // const changeLanguage = (langCode) => {
  //   i18n.changeLanguage(langCode);
  //   setSelectedLanguage(langCode);
  //   setLanguageOpen(false);
  //   try {
  //     localStorage.setItem(I18N_LANGUAGE_KEY, langCode);
  //   } catch (_) {
  //     // storage disabled
  //   }
  // };

  // React.useEffect(() => {
  //   setSelectedLanguage(i18n.language || 'en');
  // }, [i18n.language]);

  React.useEffect(() => {
    window.addEventListener("resize", updateColor);
    return function cleanup() {
      window.removeEventListener("resize", updateColor);
    };
  });
  // Tema 2 açık tema mobil: collapse açıkken bg-white; Tema 1 koyu şerit (eski davranış)
  const useWhiteNavbarOnMobileCollapse = isTheme2Shell && isLightTheme;

  // function that adds color white/transparent to the navbar on resize (this is for the collapse)
  const updateColor = () => {
    if (window.innerWidth < 993 && collapseOpen) {
      setColor(useWhiteNavbarOnMobileCollapse ? "bg-white" : "navbar-transparent");
    } else {
      setColor("navbar-transparent");
    }
  };
  // this function opens and closes the collapse on small devices
  const toggleCollapse = () => {
    if (collapseOpen) {
      setColor("navbar-transparent");
    } else {
      setColor(useWhiteNavbarOnMobileCollapse ? "bg-white" : "navbar-transparent");
    }
    setCollapseOpen(!collapseOpen);
  };

  const closeMobileNavbarCollapse = React.useCallback(() => {
    setCollapseOpen(false);
    setColor("navbar-transparent");
  }, []);

  React.useEffect(() => {
    if (!isMobileNavbarLayout || !collapseOpen) return undefined;

    const handlePointerDownOutside = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest(MOBILE_NAVBAR_SHELL_SELECTOR)) return;
      if (target.closest(".admin-navbar-mobile-collapse-backdrop")) return;
      if (target.closest(".modal.show")) return;
      if (target.closest(".dropdown-menu.show")) return;
      closeMobileNavbarCollapse();
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") closeMobileNavbarCollapse();
    };

    document.addEventListener("mousedown", handlePointerDownOutside, true);
    document.addEventListener("touchstart", handlePointerDownOutside, true);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handlePointerDownOutside, true);
      document.removeEventListener("touchstart", handlePointerDownOutside, true);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isMobileNavbarLayout, collapseOpen, closeMobileNavbarCollapse]);

  React.useEffect(() => {
    if (sidebarOpened && isMobileNavbarLayout && collapseOpen) {
      closeMobileNavbarCollapse();
    }
  }, [sidebarOpened, isMobileNavbarLayout, collapseOpen, closeMobileNavbarCollapse]);

  const getRoutesData = () => {
    const data = [];
    let id = 1;

    const processRoutes = (routes, parentCategory = '') => {
      routes.forEach(route => {
        // Skip invisible routes
        if (route.invisible) {
          return;
        }

        // Permission kontrolü: Eğer permission varsa ve kullanıcıda yoksa, gösterme
        if (route.permission && account && !account.is_super_admin && account?.permissions && !account?.permissions[route.permission]) {
          return;
        }

        // Tenant web_enabled overrides web_maker permission
        if (isWebMakerRouteBlocked(account, route)) {
          return;
        }

        if (route.name && route.path) {
          // Determine category based on route structure
          let category = parentCategory;
          if (route.collapse) {
            category = route.name;
          } else if (!category) {
            category = 'Main';
          }

          // Get icon from route or use default
          let icon = 'tim-icons icon-paper';

          // First check if route has its own icon
          if (route.icon) {
            icon = route.icon;
          } else {
            // If no icon, determine based on route name and category
            const routeName = route.name.toLowerCase();
            const category = parentCategory.toLowerCase();

            // Inventory related
            if (category.includes('inventory') || routeName.includes('inventory')) {
              if (routeName.includes('add')) icon = 'tim-icons icon-simple-add';
              else if (routeName.includes('search')) icon = 'tim-icons icon-zoom-split';
              else if (routeName.includes('status')) icon = 'tim-icons icon-bullet-list-67';
              else if (routeName.includes('buyer')) icon = 'tim-icons icon-single-02';
              else if (routeName.includes('queue')) icon = 'tim-icons icon-notes';
              else if (routeName.includes('evaluate')) icon = 'tim-icons icon-chart-pie-36';
              else icon = 'tim-icons icon-bag-16';
            }
            // Customers related
            else if (category.includes('customer') || routeName.includes('customer')) {
              if (routeName.includes('add')) icon = 'tim-icons icon-simple-add';
              else if (routeName.includes('search')) icon = 'tim-icons icon-zoom-split';
              else if (routeName.includes('list')) icon = 'tim-icons icon-bullet-list-67';
              else if (routeName.includes('detail')) icon = 'tim-icons icon-single-02';
              else icon = 'tim-icons icon-single-02';
            }
            // Reports related
            else if (category.includes('report') || routeName.includes('report')) {
              if (routeName.includes('daily')) icon = 'tim-icons icon-calendar-60';
              else if (routeName.includes('tax')) icon = 'tim-icons icon-money-coins';
              else if (routeName.includes('e-cabinet')) icon = 'tim-icons icon-book-bookmark';
              else icon = 'tim-icons icon-notes';
            }
            // Settings related
            else if (category.includes('setting') || routeName.includes('setting')) {
              if (routeName.includes('role')) icon = 'tim-icons icon-single-02';
              else if (routeName.includes('institution')) icon = 'tim-icons icon-bank';
              else if (routeName.includes('channel')) icon = 'tim-icons icon-sound-wave';
              else if (routeName.includes('portfolio')) icon = 'tim-icons icon-book-bookmark';
              else if (routeName.includes('queue')) icon = 'tim-icons icon-bullet-list-67';
              else if (routeName.includes('flag')) icon = 'tim-icons icon-tag';
              else if (routeName.includes('equipment')) icon = 'tim-icons icon-controller';
              else if (routeName.includes('communication')) icon = 'tim-icons icon-chat-33';
              else if (routeName.includes('dealer')) icon = 'tim-icons icon-bank';
              else if (routeName.includes('location')) icon = 'tim-icons icon-pin';
              else icon = 'tim-icons icon-settings';
            }
            // Users related
            else if (category.includes('user') || routeName.includes('user')) {
              if (routeName.includes('add')) icon = 'tim-icons icon-simple-add';
              else if (routeName.includes('detail')) icon = 'tim-icons icon-single-02';
              else icon = 'tim-icons icon-single-02';
            }
            // Authentication related
            else if (category.includes('auth') || routeName.includes('login') || routeName.includes('register')) {
              icon = 'tim-icons icon-lock-circle';
            }
            // Dashboard
            else if (routeName.includes('dashboard')) {
              icon = 'tim-icons icon-chart-pie-36';
            }
            // Default
            else {
              icon = 'tim-icons icon-paper';
            }
          }

          data.push({
            id: id++,
            type: 'page',
            title: route.name,
            path: route.path,
            icon: icon,
            category: category
          });
        }

        // Process nested routes (views) - but only if this route has collapse=true
        if (route.collapse && route.views && Array.isArray(route.views)) {
          processRoutes(route.views, route.name);
        }
      });
    };

    if (routes && Array.isArray(routes)) {
      processRoutes(routes);
    }

    return data;
  };

  const routesData = getRoutesData();

  const toggleModalSearch = () => {
    setModalSearch(!modalSearch);
    if (!modalSearch) {
      setSearchQuery('');
      const defaultItems = routesData.slice(0, 10);
      setSearchSections([
        {
          key: 'pages',
          title: 'Pages',
          items: defaultItems,
        },
      ]);
    }
  };

  // Focus search input when the modal opens (including Enter key open)
  React.useEffect(() => {
    if (modalSearch) {
      const id = requestAnimationFrame(() => {
        if (searchInputRef && searchInputRef.current && typeof searchInputRef.current.focus === 'function') {
          searchInputRef.current.focus();
        }
      });
      return () => cancelAnimationFrame(id);
    }
  }, [modalSearch]);

  // Open search modal on Enter key when not typing in an input field
  React.useEffect(() => {
    const handleEnterKeyToOpenSearch = (e) => {
      if (e.key !== 'Enter') return;

      // If modal is already open: close it when no query is typed
      if (modalSearch) {
        if (!searchQuery || searchQuery.trim() === '') {
          e.preventDefault();
          toggleModalSearch();
        }
        return;
      }

      // If modal is closed: open only when not typing in form fields
      const active = document.activeElement;
      const tagName = active && active.tagName ? active.tagName.toLowerCase() : '';
      const isEditable = active && (active.isContentEditable || (active.getAttribute && active.getAttribute('contenteditable') === 'true'));
      const isTyping = tagName === 'input' || tagName === 'textarea' || tagName === 'select' || isEditable;
      if (isTyping) return;

      e.preventDefault();
      toggleModalSearch();
    };
    window.addEventListener('keydown', handleEnterKeyToOpenSearch);
    return () => window.removeEventListener('keydown', handleEnterKeyToOpenSearch);
  }, [modalSearch, searchQuery]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (!query) {
      const defaultItems = routesData.slice(0, 10);
      setSearchSections([
        {
          key: 'pages',
          title: 'Pages',
          items: defaultItems,
        },
      ]);
    }
  };

  // Normalize API search results to UI shape
  React.useEffect(() => {
    if (!searchQuery) return;
    const results = searchData?.results || searchData?.data || searchData || {};
    const sections = [];

    const addSection = (key, title, arr, mapItem) => {
      if (Array.isArray(arr) && arr.length > 0) {
        const items = arr.slice(0, 10).map(mapItem);
        if (items.length > 0) {
          sections.push({ key, title, items });
        }
      }
    };

    // Inventory
    addSection('inventory', 'Inventory', results.inventory, (item, idx) => ({
      id: item.id || idx + 1,
      title:
        (item.stock_number ? `${item.stock_number}` : '') +
        (item.vin ? (item.stock_number ? ` • ${item.vin}` : item.vin) : ''),
      path: `/inventory/${item.documentId}`,
      category: 'Inventory',
      icon: <RiCarFill />,
      statusName: item.inventory_vehicle?.vehicle_status?.status_name,
      statusKey: item.inventory_vehicle?.vehicle_status?.status_key,
      statusColor: item.inventory_vehicle?.vehicle_status?.color,
    }));

    // Customers
    addSection('customers', 'Customers', results.customers, (item, idx) => ({
      id: item.id || idx + 1,
      title: item.full_name,
      path: `/customers/${item.customer_id}`,
      category: 'Customers',
      icon: 'tim-icons icon-single-02',
    }));

    // Deals
    addSection('deals', 'Deals', results.deals, (item, idx) => ({
      id: item.id || idx + 1,
      title: `Deal ${item.deal_id}`,
      path: `/deals/${item.deal_id}`,
      category: 'Deals',
      icon: 'tim-icons icon-credit-card',
    }));

    // Calculus
    addSection('accounts', 'Accounts', results.calculus, (item, idx) => ({
      id: item.id || idx + 1,
      title: `Account ${item.booking_id || idx + 1}`,
      path: `/accounts/${item.booking_id}`,
      category: 'Accounts',
      icon: 'tim-icons icon-chart-pie-36',
    }));

    setSearchSections(sections);
  }, [searchData, searchQuery]);

  const totalSearchItems = React.useMemo(() => {
    return (searchSections || []).reduce((sum, s) => sum + (Array.isArray(s.items) ? s.items.length : 0), 0);
  }, [searchSections]);

  // Handle search result click
  const handleResultClick = (result) => {
    setModalSearch(false);
    setSearchQuery('');
    setSearchSections([]);
    navigate(result.path);
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Inventory':
        return '#1f8ef1';
      case 'Customers':
        return '#00f2c3';
      case 'Reports':
        return '#ff8d72';
      case 'Settings':
        return '#8965e0';
      case 'Users':
        return '#fb6340';
      case 'Main':
        return '#2dce89';
      default:
        return '#6c757d';
    }
  };

  return (
    <>
      {isMobileNavbarLayout && collapseOpen && !embedInAppearancePreview && (
        <button
          type="button"
          className="admin-navbar-mobile-collapse-backdrop"
          aria-label={t("common.close")}
          tabIndex={-1}
          onClick={closeMobileNavbarCollapse}
        />
      )}
      <Navbar
        className={classNames({
          "navbar-fixed": !embedInAppearancePreview,
          "admin-navbar-mobile-menu-open": isMobileNavbarLayout && collapseOpen,
          [color]:
            typeof location?.pathname !== "string" ||
            location.pathname.indexOf("full-screen-map") === -1,
          ...appearance.navbarShellClassNames,
        })}
        expand="lg"
      >
        <Container fluid>
          <div
            className={classNames("navbar-wrapper", appearance.navbarWrapperShellClassNames)}
          >
            <div
              className={classNames("navbar-toggle d-inline", {
                toggled: theme2DrawerLayout
                  ? sidebarOpened
                  : sidebarHamburgerIsMiniToggle
                    ? sidebarMini
                    : sidebarOpened,
              })}
            >
              <button
                type="button"
                className={appearance.sidebarToggleClassName}
                onClick={sidebarHamburgerIsMiniToggle ? handleMiniClick : toggleSidebar}
                aria-label={
                  sidebarHamburgerIsMiniToggle
                    ? t("common.toggleSidebarMini")
                    : t("common.toggleSidebar")
                }
              >
                {isTheme2Shell ? (
                  <RxHamburgerMenu
                    size={28}
                    style={{
                      color: appearance.toolbarIconColor,
                      display: "block",
                      flexShrink: 0,
                    }}
                    aria-hidden
                  />
                ) : (
                  <>
                    <span className="navbar-toggler-bar bar1" />
                    <span className="navbar-toggler-bar bar2" />
                    <span className="navbar-toggler-bar bar3" />
                  </>
                )}
              </button>
            </div>
            {logo && displayNavbarLogo && (
              <div className="navbar-logo d-inline"
              >
                <img
                  src={navbarLogoSrc}
                  alt="react-logo"
                  style={navbarLogoImgStyle}
                  onClick={() => navigate('/dashboard')}
                />
              </div>
            )}
            {displayNavbarBrand && (
              <NavbarBrand
                style={appearance.brand}
                href="#pablo"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/dashboard");
                }}
              >
                DealertIQ
              </NavbarBrand>
            )}
          </div>
          <button
            className={classNames("navbar-toggler", {
              "dtiq-theme2-navbar-mobile-kebab": isTheme2Shell,
              "admin-navbar-mobile-kebab-open": collapseOpen,
            })}
            type="button"
            data-toggle="collapse"
            data-target="#navigation"
            aria-expanded={collapseOpen}
            aria-label={collapseOpen ? t("common.close") : "Toggle navigation"}
            onClick={toggleCollapse}
          >
            {collapseOpen ? (
              <IoClose size={22} color={mobileKebabIconColor} aria-hidden />
            ) : (
              <>
                <span className="navbar-toggler-bar navbar-kebab" />
                <span className="navbar-toggler-bar navbar-kebab" />
                <span className="navbar-toggler-bar navbar-kebab" />
              </>
            )}
          </button>

          <Collapse navbar isOpen={collapseOpen}>
            <Nav
              className={classNames(
                "d-none d-lg-flex",
                appearance.desktopSearchNavExtraClassName
              )}
              navbar
              style={appearance.centerNav}
            >
              <div
                className={classNames({
                  "dtiq-theme2-navbar-search-trigger": isTheme2Shell,
                  "dtiq-theme2-light-search-trigger": lightNavbarSurface,
                })}
                onClick={toggleModalSearch}
                style={appearance.searchTrigger}
                onMouseEnter={
                  lightNavbarSurface
                    ? undefined
                    : (e) => {
                        e.currentTarget.style.backgroundColor = appearance.searchTriggerHoverBg;
                        if (appearance.syncSearchTriggerBorderWithBackground) {
                          e.currentTarget.style.borderColor = appearance.searchTriggerHoverBg;
                        }
                      }
                }
                onMouseLeave={
                  lightNavbarSurface
                    ? undefined
                    : (e) => {
                        e.currentTarget.style.backgroundColor = appearance.searchTriggerDefaultBg;
                        if (appearance.syncSearchTriggerBorderWithBackground) {
                          e.currentTarget.style.borderColor = appearance.searchTriggerDefaultBg;
                        }
                      }
                }
              >
                <i className="tim-icons icon-zoom-split" style={appearance.searchIcon} />
                <span
                  className={lightNavbarSurface ? "dtiq-theme2-light-search-placeholder" : undefined}
                  style={
                    isTheme2Shell
                      ? { ...appearance.searchPlaceholder, ...THEME2_SHELL_NAV_TYPOGRAPHY }
                      : appearance.searchPlaceholder
                  }
                >
                  Search...
                </span>
                {appearance.showSearchEnterBadge !== false && (
                  <div style={appearance.searchEnterWrap}>
                    <span
                      className={lightNavbarSurface ? "dtiq-theme2-light-search-enter" : undefined}
                      style={appearance.searchEnterBadge}
                    >
                      Enter
                    </span>
                  </div>
                )}
              </div>
            </Nav>
            {/* Desktop Navigation */}
            <Nav className="ml-auto d-none d-lg-flex" navbar
              style={appearance.rightNav}
            >
              {!isTheme2Shell ? (
                <div style={appearance.rightNavItemWrap}>
                  <NavbarHelp
                    isOpen={helpOpen}
                    onToggle={setHelpOpen}
                    activeColor={activeColor}
                    isLightTheme={isLightTheme}
                    toolbarIconFontSize={appearance.toolbarIconFontSize}
                    toolbarIconColor={appearance.toolbarIconColor}
                    toolbarIconColorActive={appearance.toolbarIconColorActive}
                  />
                </div>
              ) : null}
              {isTheme2Shell && (
                <div style={appearance.rightNavItemWrap}>
                  <NavbarLanguageSelector
                    lightNavbarSurface={lightNavbarSurface}
                    toolbarIconFontSize={
                      appearance.languageToolbarIconFontSize ?? appearance.toolbarIconFontSize
                    }
                    toolbarIconColor={appearance.toolbarIconColor}
                    toolbarIconColorActive={appearance.toolbarIconColorActive}
                  />
                </div>
              )}
              <div style={appearance.rightNavItemWrap}>
                <Notification
                  activeColor={activeColor}
                  sidebarMini={sidebarMini}
                  handleActiveClick={handleActiveClick}
                  handleMiniClick={handleMiniClick}
                  lightNavbarSurface={lightNavbarSurface}
                  theme2Shell={isTheme2Shell}
                  toolbarIconFontSize={appearance.toolbarIconFontSize}
                  toolbarIconColor={appearance.toolbarIconColor}
                  toolbarIconColorActive={appearance.toolbarIconColorActive}
                />
              </div>
              <div style={appearance.rightNavItemWrap}>
                <FutureFeature
                  activeColor={activeColor}
                  sidebarMini={sidebarMini}
                  handleActiveClick={handleActiveClick}
                  handleMiniClick={handleMiniClick}
                  lightNavbarSurface={lightNavbarSurface}
                  toolbarIconFontSize={appearance.toolbarIconFontSize}
                  toolbarIconColor={appearance.toolbarIconColor}
                  toolbarIconColorActive={appearance.toolbarIconColorActive}
                />
              </div>
              {isTheme2Shell && (
                <div style={appearance.rightNavItemWrap}>
                  <a
                    href="#pablo"
                    aria-label={isFullscreen ? t("common.exitFullscreen") : t("common.enterFullscreen")}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleDocumentFullscreen();
                    }}
                    onMouseEnter={() => setT2FullscreenHoverDesktop(true)}
                    onMouseLeave={() => setT2FullscreenHoverDesktop(false)}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "40px",
                      height: "40px",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {isFullscreen ? (
                      <MdOutlineFullscreenExit
                        size={fullscreenIconPx}
                        color={t2FullscreenTriggerIconColor(t2FullscreenHoverDesktop)}
                      />
                    ) : (
                      <MdFullscreen
                        size={fullscreenIconPx}
                        color={t2FullscreenTriggerIconColor(t2FullscreenHoverDesktop)}
                      />
                    )}
                  </a>
                </div>
              )}
              <div style={appearance.accountMenuWrap}>
                <AccountUserMenu
                  variant="navbar"
                  closeSidebar={closeSidebar}
                  closeMobileNavbar={closeMobileNavbarCollapse}
                  lightNavbarSurface={accountMenuLightSurface}
                  hideLanguageInUserMenu={isTheme2Shell}
                  helpInUserMenu={isTheme2Shell}
                  theme2Shell={isTheme2Shell}
                  applyShellNavTypographyVars={isTheme2Shell}
                  onHelpToggle={setHelpOpen}
                />
              </div>
            </Nav>

            {/* Mobile Navigation - Centered */}
            <Nav className="d-lg-none d-flex justify-content-center w-100" navbar>
              <div className="d-flex align-items-center justify-content-center" style={appearance.mobileToolsRow}>
                {isTheme2Shell && (
                  <a
                    href="#pablo"
                    aria-label={isFullscreen ? t("common.exitFullscreen") : t("common.enterFullscreen")}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleDocumentFullscreen();
                    }}
                    onMouseEnter={() => setT2FullscreenHoverMobile(true)}
                    onMouseLeave={() => setT2FullscreenHoverMobile(false)}
                    style={{
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "40px",
                      height: "40px",
                      marginRight: "8px",
                      backgroundColor: lightNavbarSurface
                        ? isFullscreen
                          ? "rgba(0, 0, 0, 0.08)"
                          : "transparent"
                        : isFullscreen
                          ? "rgba(255, 255, 255, 0.15)"
                          : "transparent",
                      transition: "all 0.2s ease",
                    }}
                  >
                    {isFullscreen ? (
                      <MdOutlineFullscreenExit
                        size={fullscreenIconPx}
                        color={t2FullscreenTriggerIconColor(t2FullscreenHoverMobile)}
                      />
                    ) : (
                      <MdFullscreen
                        size={fullscreenIconPx}
                        color={t2FullscreenTriggerIconColor(t2FullscreenHoverMobile)}
                      />
                    )}
                  </a>
                )}
                {isTheme2Shell && (
                  <div style={{ ...appearance.rightNavItemWrap, marginRight: "4px" }}>
                    <NavbarLanguageSelector
                      lightNavbarSurface={lightNavbarSurface}
                      toolbarIconFontSize={
                        appearance.languageToolbarIconFontSize ?? appearance.toolbarIconFontSize
                      }
                      toolbarIconColor={appearance.toolbarIconColor}
                      toolbarIconColorActive={appearance.toolbarIconColorActive}
                    />
                  </div>
                )}

                {/* <Button
                  color="link"
                  data-target="#searchModal"
                  data-toggle="modal"
                  id="search-button"
                  onClick={toggleModalSearch}
                  style={{ padding: '8px' }}
                >
                  <i className="tim-icons icon-zoom-split" />
                </Button>
                 */}
                {/* <Notification
                  activeColor={activeColor}
                  sidebarMini={sidebarMini}
                  handleActiveClick={handleActiveClick}
                  handleMiniClick={handleMiniClick}
                />
                 */}
                <AccountUserMenu
                  variant="navbar"
                  closeSidebar={closeSidebar}
                  closeMobileNavbar={closeMobileNavbarCollapse}
                  mobileNavbarCollapseOpen={collapseOpen}
                  lightNavbarSurface={accountMenuLightSurface}
                  hideLanguageInUserMenu={isTheme2Shell}
                  helpInUserMenu={isTheme2Shell}
                  theme2Shell={isTheme2Shell}
                  applyShellNavTypographyVars={isTheme2Shell}
                  onHelpToggle={setHelpOpen}
                />
              </div>
            </Nav>
          </Collapse>
          {isTheme2Shell && (
            <NavbarHelp
              hideToolbarTrigger
              isOpen={helpOpen}
              onToggle={setHelpOpen}
              activeColor={activeColor}
              isLightTheme={isLightTheme}
              toolbarIconFontSize={appearance.toolbarIconFontSize}
              toolbarIconColor={appearance.toolbarIconColor}
              toolbarIconColorActive={appearance.toolbarIconColorActive}
            />
          )}

        </Container>
      </Navbar>

      <Modal
        modalClassName="modal-search"
        isOpen={modalSearch}
        toggle={toggleModalSearch}
        style={appearance.modalRoot}
      >
        <div className="modal-header" style={appearance.modalHeader}>
          <div style={appearance.modalSearchRow}>
            <i className="tim-icons icon-zoom-split" style={appearance.modalSearchIcon}></i>
            <Input
              id="inlineFormInputGroup"
              placeholder="Search..."
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              autoFocus
              innerRef={searchInputRef}
              style={
                isTheme2Shell
                  ? { ...appearance.modalInput, ...THEME2_SHELL_NAV_TYPOGRAPHY }
                  : appearance.modalInput
              }
            />
          </div>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={toggleModalSearch}
          >
            <i className="tim-icons icon-simple-remove" style={appearance.modalCloseIcon} />
          </button>
        </div>
        <div style={appearance.modalBody}>
          {searchSections && searchSections.length > 0 && totalSearchItems > 0 ? (
            searchSections.map((section) => (
              <div key={section.key}>
                <div style={appearance.sectionHeader}>
                  {section.title}
                </div>
                {section.items.map((result) => (
                  <div
                    key={result.id}
                    style={appearance.resultRow}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = appearance.resultRowHoverBg;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = appearance.resultRowDefaultBg;
                    }}
                    onClick={() => handleResultClick(result)}
                  >
                    <div style={appearance.resultRowInner}>
                      <div
                        style={{
                          ...appearance.resultIconWrapBase,
                          backgroundColor: getCategoryColor(result.category),
                        }}
                      >
                        {typeof result.icon === 'string' ? (
                          <i className={result.icon} style={appearance.resultIconFont}></i>
                        ) : (
                          React.cloneElement(result.icon, { size: 15 })
                        )}
                      </div>
                      <div
                        style={
                          isTheme2Shell
                            ? { ...appearance.resultTextCol, ...THEME2_SHELL_NAV_TYPOGRAPHY }
                            : appearance.resultTextCol
                        }
                      >
                        <div
                          style={
                            isTheme2Shell
                              ? {
                                  ...appearance.resultTitle,
                                  fontSize: "1em",
                                  lineHeight: "inherit",
                                }
                              : appearance.resultTitle
                          }
                        >
                          {result.title}
                        </div>
                        <div
                          style={
                            isTheme2Shell
                              ? { ...appearance.resultPath, fontSize: "0.88em", lineHeight: "inherit" }
                              : appearance.resultPath
                          }
                        >
                          {result.path}
                        </div>
                      </div>
                      <div style={appearance.resultMetaCol}>
                        <div
                          style={{
                            ...appearance.resultCategory,
                            color: getCategoryColor(result.category),
                          }}
                        >
                          {result.category}
                        </div>
                        {result.statusName && (
                          <div
                            style={{
                              ...appearance.resultStatus,
                              color: result.statusColor || "#8898aa",
                            }}
                          >
                            {result.statusName}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))
          ) : searchQuery && searchQuery.length > 0 && !searchLoading && totalSearchItems === 0 ? (
            <div style={appearance.emptyState}>
              <i className="tim-icons icon-zoom-split" style={appearance.emptyStateIcon}></i>
              <div>No results found</div>
              <div style={appearance.emptyStateHint}>Try different keywords</div>
            </div>
          ) : null}
        </div>
      </Modal>
    </>
  );
};

export default AdminNavbar;
